import { Box, capitalize } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-pro';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { StyleObj } from '../../../../@types';
import { PaymentTransaction } from '../../../../@types/api';
import { createDescriptionColumn } from '../../../../helpers/columns';
import { usePagination } from '../../../../hooks/usePagination';
import useSort from '../../../../hooks/useSort';
import { createColumn, floorToFixed } from '../../../../utils';
import DateRangeSelect, { DayjsTimestamp } from '../../../molecules/DateRangeSelect';
import TableTemplate from '../../../templates/TableTemplate';
import { formatPaymentMethod } from '../../../../helpers/paymentDisplay';

const styles: StyleObj = {
  container: {
    position: 'relative',
    height: 'calc(100% - 200px)',
  },
};

const columns: GridColDef[] = [
  createColumn('id', 'ID', { sortable: false }),
  createColumn('updatedBy', 'Updated by', {
    valueGetter: (value, row, column, apiRef) => row.metadata?.operatorFullName,
  }),
  createColumn('executionTime', 'Date and Time', {
    valueGetter: (value, row, column, apiRef) => dayjs(row.executionTime).format('DD/MM/YYYY HH:mm:ss'),
  }),
  createColumn('type', 'Type', {
    valueGetter: (value, row, column, apiRef) => capitalize(row.type.replace('_', ' ')),
  }),
  createColumn('solution', 'Solution'),
  createColumn('paymentMethod', 'Pay Method', {
    valueFormatter: (value, row: PaymentTransaction) => formatPaymentMethod(value, row.metadata),
  }),
  createColumn('amount', 'Amount', {
    valueFormatter: (value, row, column, apiRef) => floorToFixed(value, 2),
  }),
  createColumn('status', 'Status', {
    valueGetter: (value, row, column, apiRef) => capitalize(row.status),
  }),
  createDescriptionColumn(),
];

const PaymentTransactionPage = () => {
  const [date, setDate] = useState<DayjsTimestamp>({
    fromTimestamp: dayjs().startOf('day'),
    toTimestamp: dayjs().endOf('day'),
  });

  const { userId } = useParams();
  const { data, changeQuery, updateQueryParams, isLoading, isFetching } = usePagination<PaymentTransaction>(
    'transactions/payments',
    {
      page: 1,
      limit: 25,
      userId,
      fromTimestamp: date.fromTimestamp.valueOf(),
      toTimestamp: date.toTimestamp.valueOf(),
    },
    undefined,
    'wallet'
  );

  const { handleSort } = useSort(changeQuery);

  const handleDateChange = (newDate: DayjsTimestamp) => {
    setDate(newDate);
  };

  useEffect(() => {
    changeQuery({
      fromTimestamp: date.fromTimestamp.valueOf(),
      toTimestamp: date.toTimestamp.valueOf(),
    });
  }, [date, changeQuery]);

  return (
    <Box sx={styles.container}>
      <TableTemplate
        columns={columns}
        rows={data?.items || []}
        rowCount={data?.count || 0}
        handleSort={handleSort}
        handlePaginationModelChange={updateQueryParams}
        loading={isFetching || isLoading}
        changeQuery={changeQuery}
        customTableHeaderContent={<DateRangeSelect date={date} onDateChange={handleDateChange} />}
      />
    </Box>
  );
};

export default PaymentTransactionPage;
